<template>
  <div v-if="visible">
    <h4 class="table-title">
      <button
        v-if="!groupsModified[groupId] && !categoryHasUnsavedMembers && !loading && mensajeDisponible && !fechaCorte && grupo_activo == 1"
        @click="enviarPreinvitacionGrupal"
        class="bg-purple-600 hover:bg-purple-800 text-white font-bold py-3 px-3 rounded mr-5 boton-individual-pre-invitacion"
      >
        {{ uiT('members.sendGroupInvitation') }}
      </button>

      <button
        v-if="!fechaCorte && grupo_activo == 1"
        @click="triggerFileInput"
        :class="buttonClasses"
        class="bg-green-500 hover:bg-green-700 mr-5 button-excel text-white font-bold py-1 px-3 rounded"
      >
        <div v-if="loading"><ProcesandoExcel /></div>
        <div v-else><EstadoInicialExcel /></div>
      </button>

      <span>{{ title }} ({{ count }})</span>

      <button
        v-if="$canView('superadministrador') && !fechaCorte && grupo_activo == 1 && hasRegisteredMembers"
        type="button"
        class="button-reset-category"
        @click="resetCategoryUsers"
      >
        {{ uiT('members.deleteAll') }}
      </button>

      <button v-if="sinLimite && !fechaCorte && grupo_activo == 1" @click="addBlankMember" class="button-add-record">{{ uiT('members.addRecord') }}</button>

      <button v-if="!fechaCorte && grupo_activo == 1" @click="performSave" class="button-save">
        <IconoConGuardar />
      </button>

      <input
        v-if="!fechaCorte"
        type="file"
        @change="handleFileChange"
        style="display:none"
        :id="'file-input-' + category + '-' + groupId"
      />

      <button @click="toggleAccordion" class="rotate-button">
        <div :class="{ 'rotate-180': isOpen }">
          <FlechaRotativa />
        </div>
      </button>
    </h4>

    <div v-show="isOpen" class="accordion-content">
      <div class="contenedor-general-usuarios">
        <div class="contenedor-usuarios" v-for="(member, index) in members" :key="`${category}-${index}`">
          <!-- GRID DINÁMICO -->
          <div class="fields-grid">
            <div v-for="f in activeFields" :key="f.label" class="field">
              <label class="input-label">
                {{ fieldTitle(f) }}
                <span v-if="f.requerido || f.mandatorio" class="req">*</span>
              </label>

              <!-- Select sistema: tipo de documento -->
              <select
                v-if="f.label === 'tipo_de_documento'"
                :value="member[f.label] ?? ''"
                class="input-field"
                :disabled="fechaCorte || grupo_activo == 0"
                @change="onInput(member, f, $event.target.value)"
              >
                <option value="">{{ uiT('fields.selectPlaceholder') }}</option>
                <option value="CC">{{ uiT('fields.documentCC') }}</option>
                <option value="CE">{{ uiT('fields.documentCE') }}</option>
                <option value="Pasaporte">{{ uiT('fields.documentPassport') }}</option>
              </select>

              <!-- ✅ País: select con buscador + autocorrección -->
              <div
                v-else-if="f.label === 'pais'"
                class="country-combo"
                :class="{ disabled: fechaCorte || grupo_activo == 0 }"
              >
                <input
                  class="input-field"
                  :disabled="fechaCorte || grupo_activo == 0"
                  :value="_countryDisplay(index, member)"
                  autocomplete="off"
                  @focus="_countryOpen(index, member, $event)"
                  @input="_countryInput(index, $event.target.value)"
                  @keydown.down.prevent="_countryMove(index, 1)"
                  @keydown.up.prevent="_countryMove(index, -1)"
                  @keydown.enter.prevent="_countryEnter(index, member, f)"
                  @keydown.esc.prevent="_countryEsc(index)"
                  @blur="_countryBlur(index, member, f)"
                />

                <div v-if="_countryState(index).open && _countryOptions(index).length" class="country-dd">
                  <button
                    v-for="(c, ci) in _countryOptions(index)"
                    :key="`pais-${index}-${ci}`"
                    type="button"
                    class="country-opt"
                    :class="{ on: _countryState(index).hover === ci }"
                    @mousedown.prevent="_countryPick(index, member, f, c)"
                  >
                    {{ c }}
                  </button>
                </div>
              </div>

              <!-- Select dinámico (custom o base con tipo_ui) -->
              <select
                v-else-if="(f.tipo_ui || 'input') === 'select'"
                :value="member[f.label] ?? ''"
                class="input-field"
                :disabled="fechaCorte || grupo_activo == 0"
                @change="onInput(member, f, $event.target.value)"
              >
                <option value="">{{ uiT('fields.selectPlaceholder') }}</option>
                <option
                  v-for="(opt, oi) in normalizeOptions(f.opciones)"
                  :key="`${f.label}-opt-${oi}`"
                  :value="opt"
                >
                  {{ opt }}
                </option>
              </select>

              <!-- Multi-check dinámico -->
              <div
                v-else-if="(f.tipo_ui || 'input') === 'multicheck'"
                class="multi-wrap"
                :class="{ disabled: fechaCorte || grupo_activo == 0 }"
              >
                <button
                  v-for="(opt, oi) in normalizeOptions(f.opciones)"
                  :key="`${f.label}-m-${oi}`"
                  type="button"
                  class="multi-pill"
                  :class="{ on: hasMulti(member, f.label, opt) }"
                  :disabled="fechaCorte || grupo_activo == 0"
                  @click="toggleMulti(member, f, opt)"
                >
                  {{ opt }}
                </button>
              </div>

              <!-- Input normal -->
              <input
                v-else
                :value="member[f.label] ?? ''"
                class="input-field"
                :disabled="fechaCorte || grupo_activo == 0"
                :inputmode="inputModeFor(f)"
                :autocomplete="autoCompleteFor(f)"
                @keydown="onKeydown(f, $event)"
                @paste="onPaste(member, f, $event)"
                @input="onInput(member, f, $event.target.value)"
              />
            </div>
          </div>

          <!-- Acciones -->
          <div v-if="!fechaCorte && grupo_activo == 1" class="botones-acciones">
            <button @click="removeMember(member, index)" class="button-clear" v-if="member.staff_id || sinLimite">
              <IconoConBorrar />
            </button>

            <button
              v-if="!groupsModified[groupId] && canSendPreinvitacion(member)"
              @click="enviarPreinvitacionIndividual(member)"
              class="bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-1 rounded mt-2 boton-individual-pre-invitacion"
            >
              {{ uiT('members.sendInvitation') }}
            </button>

            <div class="mx-0 px-0 mt-3" v-if="$canView('superadministrador','administradorevento')">
              <button
                v-if="canApproveComputed(member) && member.autorizado == '0'"
                @click="approveUser(member.staff_id, category)"
                class="bg-green-500 hover:bg-green-700 text-white font-bold py-1 px-3 rounded"
              >
                {{ uiT('members.approve') }}
              </button>

              <button v-if="checkPropAutorizado(member)">
                <IconoAutorizado />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import ProcesandoExcel from '../svgs/ProcesandoExcel.vue';
import EstadoInicialExcel from '../svgs/EstadoInicialExcel.vue';
import IconoConGuardar from '../svgs/IconoConGuardar.vue';
import IconoConBorrar from '../svgs/IconoConBorrar.vue';
import FlechaRotativa from '../svgs/FlechaRotativa.vue';
import IconoAutorizado from '../svgs/IconoAutorizado.vue';
import { useToast } from "vue-toastification";
import { PAISES, coerceFromList, filterFromList } from "@/utils/paises";
import { userManagementI18nMixin } from "../i18n/userManagementI18n.js";
import { getEventFieldLabel } from "../i18n/eventFieldLabels.js";

export default {
  mixins: [userManagementI18nMixin],
  props: {
    group: Object,
    groupId: [String, Number],
    category: String,
    title: String,
    count: [String, Number],
    members: Array,
    visible: Boolean,
    loading: Boolean,
    isOpen: Boolean,
    camposObligatorios: { type: Array, default: () => [] },
    fechaCorte: Boolean,
    grupo_activo: [String, Number],
    eventoToken: String,
    groupName: String,
    groupsModified: Object,
    sinLimite: { type: Boolean, default: false },
  },
  components: {
    ProcesandoExcel,
    EstadoInicialExcel,
    IconoConGuardar,
    IconoConBorrar,
    FlechaRotativa,
    IconoAutorizado
  },
  inject: ['canApproveComputed'],
  emits: ['trigger-file-input','file-changed','toggle-accordion','perform-save','approve-user','show-modal','group-modified','update-members','reset-category'],

  data() {
    return {
      buttonClasses: 'bg-green-500 hover:bg-green-700 mr-5 button-excel text-white font-bold py-1 px-3 rounded',
      mensajeDisponible: false,
      lastWarnAt: 0,

      countryUI: {},
    };
    
  },

  setup() {
    const toast = useToast();
    return { toast };
  },

  computed: {
    roleToken() {
      return this.$store.state.user.token;
    },
    activeFields() {
      return (this.camposObligatorios || []).filter(f => f && f.activo && f.label);
    },
    categoryHasUnsavedMembers() {
      return (this.members || []).some((member) => {
        if (!this.isMeaningfulMember(member)) return false;
        return !member.staff_id || member.isModified || !member.isSaved;
      });
    },
    hasRegisteredMembers() {
      return (this.members || []).some((member) => member?.staff_id && this.isMeaningfulMember(member));
    }
  },

  mounted() {
    this.verificarMensajeDinamico();
  },

  methods: {
    _countryState(index) {
      const k = String(index);
      if (!this.countryUI[k]) this.countryUI[k] = { open: false, draft: "", query: "", hover: 0 };
      return this.countryUI[k];
    },

    
    _countryDisplay(index, member) {
      const st = this._countryState(index);
      return st.open ? st.draft : String(member?.pais ?? "");
    },
    _countryOpen(index, member, e) {
      if (this.fechaCorte || this.grupo_activo == 0) return;
      const st = this._countryState(index);
      st.open = true;
      st.draft = String(member?.pais ?? "");
      st.query = "";          // ✅ IMPORTANTE: al abrir, mostramos TODOS
      st.hover = 0;

      // ✅ UX: selecciona todo para que el user escriba encima fácilmente
      window.setTimeout(() => {
        try { e?.target?.select?.(); } catch (_) {console.log('')}
      }, 0);
    },
    
    _countryInput(index, val) {
      const st = this._countryState(index);
      st.open = true;
      st.draft = String(val ?? "");
      st.query = st.draft;    // ✅ al escribir, filtramos
      st.hover = 0;
    },
    _countryOptions(index) {
      const st = this._countryState(index);

      // ✅ si NO hay query (o está en blanco), muestra el 100% (scrolleable)
      if (!String(st.query ?? "").trim()) return PAISES;

      // ✅ si ya está escribiendo, filtra y limita
      return filterFromList(st.query, PAISES, 12);
    },

    
    _countryPick(index, member, field, opt) {
      const st = this._countryState(index);
      st.draft = opt;
      st.query = opt;
      st.open = false;
      this.onInput(member, field, opt);
    },
    _countryCommit(index, member, field) {
      const st = this._countryState(index);
      const draft = String(st.draft ?? "").trim();

      if (!draft) {
        st.open = false;
        this.onInput(member, field, "");
        return;
      }

      const canon = coerceFromList(draft, PAISES);
      if (!canon) {
        st.open = false;
        st.draft = "";
        this.onInput(member, field, "");
        this.toast.warning(this.uiT('validation.invalidCountry'));
        return;
      }

      st.open = false;
      st.draft = canon;
      this.onInput(member, field, canon);
    },
    _countryBlur(index, member, field) {
      // delay para permitir click en opción (mousedown) antes de cerrar
      window.setTimeout(() => {
        const st = this._countryState(index);
        if (!st.open) return;
        this._countryCommit(index, member, field);
      }, 120);
    },
    _countryMove(index, dir) {
      const st = this._countryState(index);
      const opts = this._countryOptions(index);
      if (!opts.length) return;
      st.hover = (st.hover + dir + opts.length) % opts.length;
    },
    _countryEnter(index, member, field) {
      const st = this._countryState(index);
      const opts = this._countryOptions(index);

      if (st.open && opts.length) {
        const chosen = opts[Math.min(st.hover, opts.length - 1)];
        this._countryPick(index, member, field, chosen);
        return;
      }
      this._countryCommit(index, member, field);
    },
    _countryEsc(index) {
      const st = this._countryState(index);
      st.open = false;
    },

    normalizeOptions(opt) {
      if (Array.isArray(opt)) return opt.map(x => String(x).trim()).filter(Boolean);
      if (typeof opt === 'string') {
        return opt.split(',').map(x => x.trim()).filter(Boolean);
      }
      return [];
    },

    isMeaningfulMember(member) {
      if (!member || typeof member !== 'object') return false;

      const fields = (this.activeFields || []).filter((field) => field && field.label);
      if (fields.length) {
        return fields.some((field) => String(member?.[field.label] ?? '').trim() !== '');
      }

      return ['nombres', 'apellidos', 'correo_electronico', 'numero_de_documento'].some((key) =>
        String(member?.[key] ?? '').trim() !== ''
      );
    },

    canSendPreinvitacion(member) {
      return !this.loading
        && !!member?.staff_id
        && !member?.isModified
        && this.canApproveComputed(member)
        && this.mensajeDisponible
        && !!member?.correo_electronico
        && !!member?.nombres
        && !!member?.apellidos;
    },

    fieldTitle(field) {
      return getEventFieldLabel(field, this.uiLanguageCode());
    },

    inputModeFor(field) {
      const tipo = (field?.tipo_campo || '').toLowerCase();
      if (tipo === 'numero') return 'numeric';
      if (field?.label === 'correo_electronico' || tipo === 'email') return 'email';
      return 'text';
    },

    autoCompleteFor(field) {
      const label = field?.label || '';
      if (label === 'correo_electronico') return 'email';
      if (label === 'telefono') return 'tel';
      if (label === 'nombres') return 'given-name';
      if (label === 'apellidos') return 'family-name';
      return 'off';
    },

    _warnOnce(msg) {
      const now = Date.now();
      if (now - this.lastWarnAt < 650) return;
      this.lastWarnAt = now;
      this.toast.warning(msg);
    },

    // ========= Reglas de teclado =========
    onKeydown(field, e) {
      if (e.isComposing) return;

      const tipo = (field?.tipo_campo || '').toLowerCase();
      const label = field?.label;

      const controlKeys = new Set([
        'Backspace','Tab','Enter','Escape','Delete',
        'ArrowLeft','ArrowRight','ArrowUp','ArrowDown',
        'Home','End','Shift','Control','Alt','AltGraph','Meta','CapsLock'
      ]);
      if (controlKeys.has(e.key)) return;
      if (e.ctrlKey || e.metaKey) return;

      // NUMERO: solo dígitos
      if (tipo === 'numero') {
        if (!/^[0-9]$/.test(e.key)) {
          e.preventDefault();
          this._warnOnce(this.uiT('validation.onlyNumbers'));
        }
        return;
      }

      // DOCUMENTO: alfanum (sin guion)
      if (label === 'numero_de_documento') {
        if (!/^[a-zA-Z0-9]$/.test(e.key)) {
          e.preventDefault();
          this._warnOnce(this.uiT('validation.documentCharacters'));
        }
        return;
      }

      // SANITIZADO: alfanum + guion medio
      if (tipo === 'sanitizado') {
        if (!/^[a-zA-Z0-9-]$/.test(e.key)) {
          e.preventDefault();
          this._warnOnce(this.uiT('validation.sanitizedCharacters'));
        }
        return;
      }

      // EMAIL: bloquea espacios + chars raros
      if (label === 'correo_electronico' || tipo === 'email') {
        if (e.key === ' ') {
          e.preventDefault();
          this._warnOnce(this.uiT('validation.emailSpaces'));
          return;
        }
        if (e.key.length > 1) return;

        const key = e.key === 'Decimal' ? '.' : (e.key === 'Subtract' ? '-' : e.key);
        if (!/^[a-zA-Z0-9@._+-]$/.test(key)) {
          e.preventDefault();
          this._warnOnce(this.uiT('validation.emailCharacter'));
        }
      }
    },

    // si pegan texto, igual lo limpiamos antes de que quede
    onPaste(member, field, e) {
      if (!e?.clipboardData) return;
      const pasted = e.clipboardData.getData('text');
      const clean = this.sanitizeValue(field, pasted);
      e.preventDefault();
      this.onInput(member, field, clean);
    },

    sanitizeValue(field, value) {
      const raw = String(value ?? '');

      const tipo = (field?.tipo_campo || '').toLowerCase();
      const label = field?.label;

      if (tipo === 'numero') {
        return raw.replace(/[^0-9]/g, '');
      }

      // Documento (sin guion)
      if (label === 'numero_de_documento') {
        return raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
      }

      // Sanitizado (con guion medio)
      if (tipo === 'sanitizado') {
        return raw.toUpperCase().replace(/[^A-Z0-9-]/g, '').replace(/-+/g, '-');
      }

      // Email
      if (label === 'correo_electronico' || tipo === 'email') {
        return raw.replace(/\s+/g, '').replace(/[^a-zA-Z0-9@.\-_+]/g, '');
      }

      return raw;
    },

    // ========= Multi-check =========
    hasMulti(member, label, opt) {
      const v = String(member?.[label] ?? '').trim();
      if (!v) return false;
      const arr = v.split(',').map(x => x.trim()).filter(Boolean);
      return arr.includes(opt);
    },

    toggleMulti(member, field, opt) {
      this.$emit('group-modified', this.groupId);

      const label = field.label;
      const cur = String(member?.[label] ?? '').trim();
      const set = new Set(cur ? cur.split(',').map(x => x.trim()).filter(Boolean) : []);

      if (set.has(opt)) set.delete(opt);
      else set.add(opt);

      member[label] = Array.from(set).join(',');

      // flags
      member.isModified = true;
      member.isSaved = false;
    },

    // ========= Input normal =========
    onInput(member, field, value) {
      this.$emit('group-modified', this.groupId);

      const clean = this.sanitizeValue(field, value);
      member[field.label] = clean;

      // flags
      member.isModified = true;
      member.isSaved = false;
    },

    async verificarMensajeDinamico() {
      try {
        const res = await fetch(`/api/mensaje-personalizado/${this.eventoToken}`);
        const result = await res.json();
        const contenido = result?.data?.contenido_html;
        this.mensajeDisponible = !!contenido && contenido.trim() !== '';
      } catch (e) {
        this.mensajeDisponible = false;
      }
    },

    triggerFileInput() { this.$emit('trigger-file-input', this.groupId, this.category); },
    handleFileChange(e) { this.$emit('file-changed', e, this.groupId, this.category); },
    toggleAccordion() { this.$emit('toggle-accordion', this.groupId, this.category); },
    performSave() { this.$emit('perform-save', this.group, this.category); },
    resetCategoryUsers() {
      this.$emit('reset-category', {
        groupId: this.groupId,
        category: this.category,
        title: this.title,
        count: (this.members || []).filter((member) => member?.staff_id && this.isMeaningfulMember(member)).length,
      });
    },
    emitMembersUpdate(nextMembers) {
      this.$emit('update-members', {
        groupId: this.groupId,
        category: this.category,
        members: nextMembers,
      });
    },
    addBlankMember() {
      this.$emit('group-modified', this.groupId);
      const member = {
        escarapela: this.category === 'staff' ? 'Staff' : this.category === 'conferencistas' ? 'Conferencista' : 'Asistente',
        autorizado: '0',
        isModified: true,
        isSaved: false,
      };
      (this.activeFields || []).forEach((f) => {
        if (f?.label && !(f.label in member)) member[f.label] = '';
      });
      const nextMembers = [...(this.members || []), member];
      this.emitMembersUpdate(nextMembers);
      if (!this.isOpen) this.toggleAccordion();
    },
    removeMember(member, index) {
      if (member && member.staff_id) {
        this.showModal(member, index);
        return;
      }
      this.$emit('group-modified', this.groupId);
      const nextMembers = (this.members || []).filter((_, currentIndex) => currentIndex !== index);
      this.emitMembersUpdate(nextMembers);
    },
    showModal(member, index) { this.$emit('show-modal', this.group, this.category, index); },
    approveUser(staffId, category) { this.$emit('approve-user', staffId, category); },

    checkPropAutorizado(member) {
      return member && 'autorizado' in member && member.autorizado === "1";
    },

    async enviarPreinvitacionGrupal() {
      try {
        const payload = {
          id_grupo: this.groupId,
          nombre_grupo: this.groupName,
          token_evento: this.eventoToken,
          category: this.category,
          roleToken: this.roleToken
        };
        const res = await fetch('/api/preinvitacion/grupal', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));

        if (!res.ok || data.status === 'error') {
          throw new Error(data.error || data.message || data.mensaje || this.uiT('invitations.groupError'));
        }

        this.toast.success(this.uiT('invitations.sent'));
      } catch (e) {
        this.toast.error(e.message || this.uiT('invitations.groupError'));
      }
    },

    async enviarPreinvitacionIndividual(member) {
      try {
        const payload = {
          id_evento: this.eventoToken,
          id_grupo: this.groupId,
          staff_id: member.staff_id || null,
          nombre_completo: `${member.nombres} ${member.apellidos}`,
          numero_de_documento: `${member.numero_de_documento}`,
          nombre_grupo: this.groupName,
          correo: member.correo_electronico,
          roleToken: this.roleToken
        };
        const res = await fetch('/api/preinvitacion/individual', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));

        if (!res.ok || data.status === 'error') {
          throw new Error(data.error || data.message || data.mensaje || this.uiT('invitations.individualError'));
        }

        this.toast.success(this.uiT('invitations.sentTo', { name: payload.nombre_completo }));
      } catch (e) {
        this.toast.error(e.message || this.uiT('invitations.individualError'));
      }
    },
  }
}
</script>

<style scoped>
.table-title {
  font-size: 1rem;
  font-weight: 600;
  padding: 20px 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: rgba(155,155,155, 0.1);
  border-radius: 15px;
  margin-bottom: 20px;
}
.table-title span{ display:inline-block; min-width:200px; }

.button-reset-category{
  border:0;
  border-radius:0.45rem;
  background:#dc2626;
  color:#fff;
  font-weight:800;
  font-size:.78rem;
  padding:0.45rem 0.65rem;
  margin-left:0.75rem;
  box-shadow:1px 2px 8px rgba(220,38,38,.22);
  cursor:pointer;
}
.button-reset-category:hover{ background:#b91c1c; }

.rotate-button{
  min-height:31px;
  min-width:30px;
  box-shadow: 1px 2px 8px #999999;
  border-radius:50%;
  display:flex;
  justify-content:center;
  align-items:center;
  padding-bottom:2px;
  position:absolute;
  right:5vw;
  background:#fff;
}
.rotate-button div{ transition: transform .3s ease-in-out; }
.rotate-180{ transform: rotate(180deg); }

.accordion-content { transition: max-height 0.4s ease-in-out; overflow:hidden; }
.accordion-content .contenedor-usuarios{
  border-radius:7px;
  background: rgba(224, 224, 224, 0.05);
  margin:15px auto;
  padding:10px;
  box-shadow: 2px 2px 10px rgba(155,155,155,0.5);
  display:flex;
}

.fields-grid{
  width:100%;
  display:grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px 14px;
  align-items:start;
}

.field{ text-align:left; }

.input-label{ font-size:.75rem; color:#4b5563; }
.req{ color:#be123c; margin-left:6px; font-weight:800; }

.input-field{
  width:100%;
  border:1px solid #d1d5db;
  border-radius: .375rem;
  padding: 4px 6px;
  box-sizing: border-box;
}

.botones-acciones{
  margin-left:auto;
  padding:0 20px;
  display:flex;
  align-items:center;
  flex-direction:column;
  justify-content:center;
  max-width:120px!important;
}
.boton-individual-pre-invitacion{ font-size:.7em; }

.button-add-record{
  background: #0f766e;
  color: #fff;
  font-weight: 700;
  padding: 6px 10px;
  border-radius: 10px;
  margin-right: 12px;
  font-size: .85rem;
}
.button-add-record:hover{ background:#115e59; }

/* ===== Multi-check pills ===== */
.multi-wrap{
  display:flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px;
  border:1px solid #d1d5db;
  border-radius: .375rem;
  min-height: 34px;
  background: #fff;
}
.multi-wrap.disabled{
  opacity: .55;
  pointer-events: none;
}
.multi-pill{
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: .78rem;
  cursor: pointer;
  transition: transform .08s ease, background .12s ease, border-color .12s ease;
}
.multi-pill:hover{ transform: translateY(-1px); }
.multi-pill.on{
  background: #dbeafe;
  border-color: #60a5fa;
}

.country-combo { position: relative; width: 100%; }
.country-combo.disabled { opacity: .55; pointer-events: none; }

.country-dd{
  position:absolute;
  top: calc(100% + 4px);
  left:0; right:0;
  background:#fff;
  border:1px solid #d1d5db;
  border-radius: 12px;
  box-shadow: 0 12px 26px rgba(0,0,0,.18);
  z-index: 50;
  padding: 6px;
  max-height: 240px;
  overflow:auto;
}

.country-opt{
  width:100%;
  text-align:left;
  padding: 7px 10px;
  border-radius: 10px;
  border:none;
  background: transparent;
  cursor:pointer;
  font-size: .9rem;
}
.country-opt:hover,
.country-opt.on { background: #eef2ff; }

</style>
